import React, { useState, useEffect } from 'react';
import { BuildingLogoChip } from '@caro/shared/components';
import { translations } from '../i18n';
import type { Language } from '../i18n';
import { Z_INDEX } from '../constants/zIndex';

function getDeviceLanguage(): Language {
  const nav = navigator.language?.substring(0, 2);
  if (nav === 'nl' || nav === 'fr' || nav === 'de') return nav;
  return 'en';
}

interface WebSplashProps {
  onComplete: () => void;
}

export function WebSplash({ onComplete }: WebSplashProps) {
  const [fading, setFading] = useState(false);
  const lang = getDeviceLanguage();
  const tagline = translations[lang].onboarding.splashTagline;

  useEffect(() => {
    const fadeTimer = setTimeout(() => setFading(true), 1200);
    const doneTimer = setTimeout(() => onComplete(), 1800);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onComplete]);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--caro-primary)',
      transition: 'opacity 0.6s ease-out',
      opacity: fading ? 0 : 1,
      zIndex: Z_INDEX.BOOT,
    }}>
      {/* Building logo (Phase 2 / T2.5). At cold boot we typically don't yet
       *  have a branded logo URL, so we omit `buildingName` here — the chip
       *  renders `null` rather than printing initials on a same-color background.
       *  Once branding resolves (via `--caro-logo-url`), the chip on Login/Home
       *  pages picks it up automatically. */}
      <div style={{ marginBottom: '24px' }}>
        <BuildingLogoChip maxHeight={72} />
      </div>
      {/* Wordmark */}
      <div style={{
        fontSize: 'var(--font-heading)',
        fontWeight: 700,
        color: '#FFFFFF',
        letterSpacing: '6px',
        marginBottom: '12px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        CARO
      </div>
      {/* Tagline */}
      <div style={{
        fontSize: 'var(--font-body)',
        color: 'rgba(255,255,255,0.8)',
        fontWeight: 400,
        marginBottom: '48px',
      }}>
        {tagline}
      </div>
      {/* Spinner */}
      <div style={{
        width: '36px',
        height: '36px',
        border: '3px solid rgba(255,255,255,0.25)',
        borderTopColor: '#FFFFFF',
        borderRadius: '50%',
        animation: 'caro-splash-spin 0.8s linear infinite',
      }} />
      <style>{`
        @keyframes caro-splash-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
